import { useQueryClient } from '@tanstack/react-query'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageEyebrow,
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
} from 'components/FbUI/Page/styles'
import { WorkspaceOutlineIcon } from 'components/Layout/LeftNav/nav-icons'
import { PinToHomepageButton } from 'components/QuickAccess/PinToHomepageButton'
import { useRecordVisit } from 'components/QuickAccess/useRecordVisit'
import { PAGE_KEYS } from 'services/homepage'
import { PillTabs } from 'components/FbUI/PillTabs'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { ToolbarDensityProvider } from 'components/Tables/CommonTableCells/ToolbarDensityContext'
import FbButton from 'components/FbUI/FbButton'
import { Plus } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DocumentsTable } from '../../components/DocumentLibrary/DocumentsTable'
import { StorageQuotaDisplay } from '../../components/DocumentLibrary/StorageQuotaDisplay'
import { UploadDocumentModal } from '../../components/DocumentLibrary/UploadDocumentModal'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import {
  DownloadProvider,
  DownloadWidget,
} from '../../features/download-widget'

type DocsTab = 'all' | 'product' | 'company'

export const DocLibraryPage: React.FC = () => {
  const { preferences } = usePreferences()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [uploadOpen, setUploadOpen] = useState(false)
  const selectedTab = (location.pathname.split('/')[2] || 'all') as DocsTab

  useEffect(() => {
    if (!location.pathname.includes('/documents/')) {
      navigate('/documents/all')
    }
  }, [location.pathname, navigate])

  useRecordVisit({ targetType: 'page', pageKey: PAGE_KEYS.documents })

  // Check feature flag
  if (!preferences?.feature_flags?.enable_document_library) {
    return <div>Document Library is not enabled</div>
  }

  const handleUploadSuccess = () => {
    toast.success('Document uploaded successfully')
    // Invalidate all document table queries to refresh the tables
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === 'string' &&
        (query.queryKey[0] as string).startsWith('DOCUMENTS_'),
    })
  }

  const tabs = [
    { key: 'all' as const, title: 'All' },
    { key: 'product' as const, title: 'Product' },
    { key: 'company' as const, title: 'Company' },
  ]

  return (
    <DownloadProvider>
      <FBPage
        innerPageClassName="!bg-surface-page !p-[8px]"
        containerClassName="!bg-surface-page !shadow-none !px-[16px] !py-[12px]"
      >
        <FBPageHeader className="!mb-6">
          <FBPageTitleRow>
            <div className="flex flex-col items-start">
              <FBPageEyebrow>
                <WorkspaceOutlineIcon size={14} />
                Workspace
              </FBPageEyebrow>
              <FBPageTitle className="!text-fg">Documents</FBPageTitle>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <FbButton
                className="!bg-brand-bright !text-white hover:!bg-brand-active"
                onClick={() => setUploadOpen(true)}
              >
                <Plus size={16} />
                Upload Document
              </FbButton>
              <PinToHomepageButton
                target={{ targetType: 'page', pageKey: PAGE_KEYS.documents }}
              />
            </div>
          </FBPageTitleRow>
        </FBPageHeader>

        <TableLinkStyleProvider variant="plain">
          <FBPageTableContainer
            className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
            style={{
              height: 'calc(var(--base-page-table-height) - 10px)',
            }}
          >
            <div className="shrink-0 flex items-center justify-between gap-4 border-b border-neutral-200 bg-white px-4 py-3">
              <PillTabs<DocsTab>
                tabs={tabs}
                activeKey={selectedTab}
                onChange={(value) => {
                  navigate(`/documents/${value}`)
                }}
                idPrefix="documents"
                aria-label="Documents views"
                size="sm"
                className="w-fit"
              />
              <StorageQuotaDisplay inline />
            </div>

            {/* sm toolbar density (32px search + Filter/Columns), matching the
                other table pages. */}
            <ToolbarDensityProvider value="sm">
              {selectedTab === 'all' && (
                <DocumentsTable
                  tableKey="DOCUMENTS_ALL_TABLE"
                  filterIdentifier="DOCUMENTS_ALL_TABLE"
                  tabType="all"
                />
              )}
              {selectedTab === 'product' && (
                <DocumentsTable
                  tableKey="DOCUMENTS_PRODUCT_TABLE"
                  filterIdentifier="DOCUMENTS_PRODUCT_TABLE"
                  tabType="product"
                />
              )}
              {selectedTab === 'company' && (
                <DocumentsTable
                  tableKey="DOCUMENTS_COMPANY_TABLE"
                  filterIdentifier="DOCUMENTS_COMPANY_TABLE"
                  tabType="company"
                />
              )}
            </ToolbarDensityProvider>
          </FBPageTableContainer>
        </TableLinkStyleProvider>

        <UploadDocumentModal
          isOpen={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onSuccess={handleUploadSuccess}
        />
      </FBPage>
      <DownloadWidget />
    </DownloadProvider>
  )
}
